/**
 * TanStack hooks over `repair.*` (ACT2-1286 PR 2b). Every write invalidates the item's own query and
 * its list, which the caller names through `invalidateKeys` (derived from the oRPC helpers).
 */
import type { InferRouterOutputs } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import type { RepairItemRef } from '@server/warehouse-repair';
import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

type RepairOutputs = InferRouterOutputs<AppRouter>['repair'];
export type RepairProposal = RepairOutputs['propose'];
export type RepairCandidate = Extract<RepairProposal['search'], { outcome: 'proposed' }>['proposal'];
export type RepairConfirmResult = RepairOutputs['confirm'];

export { isAccountNeedsRepairError, isRepairConflictError, refusalVerdict } from './repairErrors';

/**
 * Open Repair: the server probes every candidate. Never cached — each open searches again — and never
 * retried, because a retry would run every probe twice. Closing the dialog unmounts the query, which
 * aborts the request.
 */
export function useRepairProposal(itemRef: RepairItemRef, enabled: boolean) {
  const orpc = useOrpc();
  return useQuery({
    ...orpc.repair.propose.queryOptions({ input: { ref: itemRef } }),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

function useInvalidate(invalidateKeys: readonly QueryKey[]): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all(invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  };
}

// Confirm's mutation-level onSuccess starts the invalidation and does NOT return its promise. TanStack awaits
// a returned promise before it runs the caller's per-call onSuccess, and the refetch it waits for can
// unmount the dialog first (the repaired item no longer needs repair), which drops the per-call callback
// and with it the confirmation toast or an audience's sync warning.
export function useConfirmRepair(invalidateKeys: readonly QueryKey[]) {
  const orpc = useOrpc();
  const invalidate = useInvalidate(invalidateKeys);
  return {
    invalidate,
    mutation: useMutation(
      orpc.repair.confirm.mutationOptions({
        onSuccess: () => {
          void invalidate();
        },
      }),
    ),
  };
}

// Dismiss keeps awaiting its invalidation: it has no per-call callback to lose, and staying pending until
// the refetch has removed the notice stops a second click from dismissing an already-dismissed notice
// (NOT_FOUND, read as "This notice changed").
export function useDismissRelinkNotice(invalidateKeys: readonly QueryKey[]) {
  const orpc = useOrpc();
  const invalidate = useInvalidate(invalidateKeys);
  return {
    invalidate,
    mutation: useMutation(orpc.repair.dismissNotice.mutationOptions({ onSuccess: () => invalidate() })),
  };
}
